import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi, type Tenant } from '../../api/tenants';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-800',
  suspended: 'bg-amber-100 text-amber-800',
  archived: 'bg-slate-100 text-slate-600',
  deleting: 'bg-red-100 text-red-800',
};

export function TenantListPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = query.trim()
        ? (await tenantApi.search({ q: query.trim(), ...(status ? { status } : {}) })).results
        : await tenantApi.list(status ? { status, q: query || undefined } : { q: query || undefined });
      setTenants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    void load();
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tenants</h1>
          <p className="mt-1 text-sm text-slate-600">Manage operational boundaries under your organization.</p>
        </div>
        <Link to="/tenants/new" className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
          Create tenant
        </Link>
        <Link to="/tenants/search" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
          Advanced search
        </Link>
      </div>

      <form onSubmit={onSearch} className="mt-6 flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Search name, GST, PAN, tags…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-300 px-3 py-2"
          aria-label="Search tenants"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        <button type="submit" className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Search</button>
      </form>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {loading ? (
        <p className="mt-6 text-slate-600">Loading…</p>
      ) : tenants.length === 0 ? (
        <p className="mt-6 text-slate-600">No tenants found.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Legal name</th>
                <th className="px-4 py-3 font-medium">Country</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/tenants/${t.id}`} className="font-medium text-slate-900 hover:underline">{t.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[t.status] ?? ''}`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.business?.legal_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.business?.country || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
